/* Typ cíle podle názvu a pořadí cílů podle horizontu.
 * ============================================================================
 * Čistá logika bez Reactu — proto tady, a ne ve stránce: ze stránky by se to
 * nedalo testovat bez celého routeru a grafů.
 *
 * Obojí nahlásil majitel 23. 9. 2026 při práci s reálným klientem:
 *   • napsal „Nákup auta" a musel ještě ručně přepnout typ na Auto;
 *   • zadal autu 7 let a cíl zůstal viset na konci seznamu za rentou (25 let).
 */

/* TYP CÍLE PODLE NÁZVU — poradce napíše „Nákup auta" a nemusí ještě přepínat
 * rozbalovátko. Rozpoznává se jen z názvu, který poradce právě píše.
 *
 * ⚠️ NIKDY NEPŘEPÍŠE TYP, KTERÝ VYBRAL ČLOVĚK. Mění se jen typ, který
 * odvodila appka (`typJeOdvozeny` níž) — jinak by se poradci pod rukama
 * měnilo nastavení: vybral by „Investice", pak jen upravil název a typ by mu
 * uskočil. Ruční volba v rozbalovátku se značí `typRucne: true`.
 *
 * Pořadí vzorů ROZHODUJE. „Rekonstrukce bytu" musí padnout na Rekonstrukci,
 * ne na Bydlení, přestože obsahuje „byt" — proto je rekonstrukce dřív.
 * Stejně tak „auto" je dřív než obecné bydlení, ať „garáž pro auto" nespadne
 * do nemovitostí. */
const VZORY_TYPU = [
  [/rekonstr|přestav|prestav|oprav|kuchyň|kuchyn|koupeln|fasád|fasad|střech|strech/i, "Rekonstrukce"],
  /* „Rezervace dovolené" NENÍ rezerva — `rezerv` bez `ac` za sebou. */
  [/rezerv(?!ac)|polštář|polstar|železn|zelezn|na horší časy|nečekan|necekan|nouz/i, "Rezerva"],
  [/\baut[oaueémy]*\b|\bvůz\b|\bvoz(?:u|em|y|ů|ech)\b|vozidl|motork|motocykl|elektromobil|karavan/i, "Auto"],
  /* PODNIKÁNÍ (majitel 29. 9. 2026). Stojí PŘED rentou, protože „penzion" by
     jinak spadl do finanční nezávislosti (vzor `penz[ei]`), a před bydlením,
     ať „byt pro podnikání" nezačne být hypotékou na bydlení.
     `podnik(?!atelsk)`: „podnikatelský úvěr" má vlastní typ níž.
     Firma jen jako PODSTATNÉ JMÉNO — „firemní auto" je pořád auto. */
  [/podnik(?!atelsk)|\bfirm(?:a|y|u|ou|ě|e|ách|ám|ami)?\b|živnost|zivnost|\bosv[čc]|\bobchod(?:u|em|ě|y|ů|ech)?\b|e-?shop|provozovn|kavárn|kavarn|restaurac|\bbistr[oau]?\b|\bsalon|startup|franšíz|fransiz|penzion|byznys|\bbusiness/i, "Podnikání"],
  [/rent[au]|nezávisl|nezavisl|důchod|duchod|penz[ei]|volnost|svobod/i, "Finanční nezávislost"],
  [/vzděl|vzdel|škol|skol|studi|univerzit|dět|det|dítě|dite|start do života/i, "Budoucnost dětí"],
  /* ⚠️ ČEŠTINA SE SKLOŇUJE. „Koupě domu" neobsahuje slovo „dům" — psát do
     vzoru jen první pád znamená, že polovina reálných názvů propadne.
     Proto kmeny s koncovkami, ale ukotvené hranicí slova, ať „domácnost"
     nebo „bytost" nespadnou do bydlení. */
  [/hypoték|hypotek|bydlen|\bdům\b|\bdom(?:u|em|ě|y|ů|ech)\b|\bbyt(?:u|em|ě|y|ů|ech)?\b|chat[auyě]|chalup|pozem|nemovitost/i, "Bydlení"],
  [/spotřebitelsk|spotrebitelsk/i, "Spotřebitelský úvěr"],
  [/podnikatelsk|firemní úvěr|firemni uver/i, "Podnikatelský úvěr"],
  [/investic|portfoli|akci[eí]|etf\b|dluhopis/i, "Investice"],
  [/úvěr|uver|půjčk|pujck|leasing/i, "Úvěr"],
];

export function typZPopisu(popis) {
  const p = String(popis || "");
  if (!p.trim()) return null;
  for (const [vzor, typ] of VZORY_TYPU) if (vzor.test(p)) return typ;
  return null;
}

/**
 * Cíle seřazené podle horizontu — nejbližší nahoře.
 *
 * ⚠️ NEŘADÍ SE PŘI PSANÍ. Kdyby se pořadí měnilo po každé stisknuté klávese,
 * řádek by poradci uskočil uprostřed zadávání let: napíše „1" z čísla 15, cíl
 * vyskočí nahoru a druhá číslice padne jinam. Stránka proto volá řazení až
 * ve chvíli, kdy poradce ŘÁDEK CÍLE OPUSTÍ (a myš je puštěná —
 * `poPusteniMysi.js`), tedy když je hodnota hotová.
 *
 * Cíle bez zadaného horizontu jdou na konec — prázdno není nula a nepatří
 * mezi cíle „na dnes". Výjimka je REZERVA: ta je z povahy věci „hned", takže
 * se řadí na začátek i bez zadaného roku (jen v POŘADÍ — horizont se jí tím
 * nevymýšlí, zobrazení dál píše „termín není stanovený"). Při shodě let jde
 * rezerva první a jinak rozhoduje dosavadní pořadí, ať se seznam zbytečně
 * nepřerovnává.
 *
 * Když se pořadí nezmění, vrací TOTÉŽ pole (stejnou referenci) — stránka
 * podle toho pozná, že nemá co ukládat, a analýza nezačne hlásit
 * „neuloženo" jen proto, že poradce proklikl řádek.
 */
export function seradCilePodleHorizontu(cile) {
  const vstup = Array.isArray(cile) ? cile : [];
  const sKlicem = vstup.map((c, i) => {
    const r = c?.roky;
    const zadano = r !== "" && r != null && Number.isFinite(+r);
    const rezerva = c?.typ === "Rezerva";
    const roky = zadano ? +r : (rezerva ? 0 : Infinity);
    return { c, i, roky, rezerva };
  });
  sKlicem.sort((a, b) => (a.roky - b.roky) || (Number(b.rezerva) - Number(a.rezerva)) || (a.i - b.i));
  const beze_zmeny = sKlicem.every((x, j) => x.i === j);
  if (beze_zmeny && Array.isArray(cile)) return cile;
  return sKlicem.map((x) => x.c);
}

/* ── ČÍM SE CÍL FINANCUJE: ÚVĚR, NEBO VLASTNÍ PENÍZE ──────────────────────────
 * Seznam žil do 25. 9. 2026 jen uvnitř `pages/AnalyzaV2Page.js`, takže Návrh
 * řešení o něm nevěděl. Majitel proto v plánu neměl jak poznat, že cíl
 * „Nové bydlení za 5 mil." appka počítá jako SPOŘENÍ, a už vůbec ne jak ho
 * přepnout — přepínač 🏦 byl jen v analýze. Karta cíle ho teď nabídne taky,
 * a nabízet ho smí jen u typů, které úvěr vůbec připouštějí.
 *
 * AUTO A PODNIKÁNÍ (majitel 29. 9. 2026): „u auta, podnikání… možnost úvěrem,
 * jako má bydlení hypotékou". Důsledek v datech je TENTÝŽ příznak `uverem`
 * — engine (planSolver `vlastniZdrojeCile`) pak spoří jen akontaci a zbytek
 * nechá na úvěru. Nic dalšího se pro ně nevymýšlí.
 *
 * ⚠️ JE TO NABÍDKA, NE AUTOMATIKA. Příznak `uverem` zůstává vypnutý, dokud ho
 * poradce nezapne. Kdyby „Bydlení" začalo být úvěrové samo od sebe, změnila by
 * se tiše čísla ve VŠECH už uložených analýzách: cílem by místo celé ceny byla
 * akontace a plán by najednou vycházel na pětinu. */
export const UVEROVE_CILE = ["Bydlení", "Úvěr", "Rekonstrukce", "Auto", "Podnikání", "Spotřebitelský úvěr", "Podnikatelský úvěr"];

/** Typ, u kterého má výběr smysl nabízet (úvěrem × hotově). */
export const jeUveroveTypu = (typ) => UVEROVE_CILE.includes(String(typ ?? ""));

/** Nemovitostní cíl = mluví se o HYPOTÉCE; u ostatních o obyčejném ÚVĚRU. */
export const jeHypotecniTypu = (typ) => typ === "Bydlení" || typ === "Rekonstrukce";

/* ── ZMĚNA TYPU A NÁZVU CÍLE — PATCHE PRO ŘÁDEK ─────────────────────────────
 * Do 29. 9. 2026 žily ve stránce. Tady proto, aby šly otestovat a aby běžný
 * i prezentační režim měnily cíl STEJNĚ (prezentace typ z názvu nepoznávala
 * vůbec).
 *
 * KDO TYP URČIL:
 *   • `typRucne: true`  — poradce ho vybral v rozbalovátku. Pak se ho název
 *                         už NIKDY nedotkne (ruční volba má přednost).
 *   • `typAuto: true`   — typ odvodila appka z názvu. Když poradce název
 *                         přepíše, typ jde s ním („Nákup auta" → „Nákup
 *                         chaty" = z Auta Bydlení).
 *   • ani jedno (starší analýzy, šablona) — typ se bere jako odvozený jen
 *     tehdy, když ho z dosavadního názvu odvodit JDE (název a typ spolu
 *     sedí). Jinak ho mohl vybrat člověk a sahat na něj nesmíme. */
export const NEUTRALNI_TYP = "Ostatní cíle";
/* Typy, u kterých znamená výběr „vždy úvěr" — automaticky zapnou příznak
 * uverem, aby to poznal i Návrh/solver. */
export const VZDY_UVER_TYP = ["Úvěr"];

/**
 * Patch pro změnu typu cíle — nastaví i závislé příznaky:
 *  • Finanční nezávislost = měsíční renta (mesicne),
 *  • Úvěr = vždy úvěrový cíl (uverem = true),
 *  • neúvěrový typ (rezerva, investice…) = uverem se vypne, jinak by na cíli
 *    zůstal viset štítek „Úvěr",
 *  • úvěrový, ale volitelný typ (Bydlení, Auto, Podnikání…) = uverem zůstává,
 *    jak si ho poradce přepnul.
 * @param {string} typ
 * @param {{rucne?: boolean}} [o] rucne = vybral ho poradce v rozbalovátku
 */
export function cilTypPatch(typ, { rucne = false } = {}) {
  const patch = { typ, mesicne: typ === "Finanční nezávislost" };
  if (VZDY_UVER_TYP.includes(typ)) patch.uverem = true;
  else if (!UVEROVE_CILE.includes(typ)) patch.uverem = false;
  if (rucne) { patch.typRucne = true; patch.typAuto = false; }
  return patch;
}

/* Cíle, které do analýzy vkládá ŠABLONA (lib/analyzaV2/model.js → EMPTY).
 * Jejich typ nevybral poradce, ale šablona — přepíše-li poradce název
 * („Bydlení / rekonstrukce" → „Rozjezd podnikání"), typ má jít s ním.
 * „Bydlení / rekonstrukce" podle vzorů vychází jako Rekonstrukce, proto tu
 * musí být výslovně. Test hlídá, že tahle mapa sedí se šablonou. */
export const SABLONOVE_CILE = {
  "Povinná likvidní rezerva": "Rezerva",
  "Bydlení / rekonstrukce": "Bydlení",
  "Vzdělání pro děti / start do života": "Budoucnost dětí",
  "Finanční nezávislost": "Finanční nezávislost",
};

/** Je typ cíle odvozený z názvu (a smí se tedy s názvem měnit)? */
export function typJeOdvozeny(cil) {
  const c = cil || {};
  if (c.typRucne === true) return false;
  if (c.typAuto === true) return true;
  if (!c.typ || c.typ === NEUTRALNI_TYP) return true;
  if (SABLONOVE_CILE[String(c.popis ?? "").trim()] === c.typ) return true;
  return typZPopisu(c.popis) === c.typ;
}

/**
 * Patch při psaní názvu cíle. Typ se změní jen tam, kde ho určila appka
 * (viz `typJeOdvozeny`), a jen když nový název ukazuje na JINÝ známý typ.
 * Ruční volbu poradce název nepřebije.
 *
 * ⚠️ NEVRACÍ SE NA „OSTATNÍ CÍLE", když název zrovna nic neříká. Poradce
 * často smaže celý název a píše ho znovu — kdyby se typ při prázdném poli
 * vracel na neutrální, zmizel by po cestě i jeho přepínač „úvěrem" a renta
 * by přestala být měsíční. Neznámý název tedy typ jen NECHÁ být — jedinou
 * výjimkou je psaní dopředu, které shodu zrušilo (`jenDopsalPoShode`).
 * @param {string} popis nový název
 * @param {object} cil dosavadní cíl (kvůli typu a příznakům)
 */
export function cilPopisPatch(popis, cil) {
  const patch = { popis };
  const c = cil && typeof cil === "object" ? cil : { typ: cil };
  if (!typJeOdvozeny(c)) return patch;
  const typ = typZPopisu(popis);
  /* Typ zůstává, ale POZNAMENÁ se, že je pořád odvozený (`typAuto`). Bez
     toho by ho první rozepsané písmeno („P" z „Podnikání") zamklo: podle
     názvu „P" už odvozený nevypadá a další písmena by typ neměnila. */
  if (!typ && jenDopsalPoShode(popis, c)) return { ...patch, ...cilTypPatch(NEUTRALNI_TYP), typAuto: true };
  if (!typ || typ === c.typ) return c.typAuto === true ? patch : { ...patch, typAuto: true };
  return { ...patch, ...cilTypPatch(typ), typAuto: true };
}

/* PSANÍ DOPŘEDU ZRUŠILO SHODU. Typ se nastavuje už během psaní, takže ho
 * může nastavit i ZAČÁTEK slova: „Rezerv" → Rezerva, ale „Rezervace
 * dovolené" rezervou není; „Obchod" → Podnikání, ale „Obchodní akademie" ne.
 * Když poradce jen PŘIPSAL znaky na konec a tím shodu zrušil, typ se vrátí
 * na neutrální. Při mazání nebo přepisování celého názvu se typ NECHÁ
 * (jinak by po cestě zmizel přepínač „úvěrem" nebo měsíční renta). */
function jenDopsalPoShode(popis, c) {
  if (!c.typ || c.typ === NEUTRALNI_TYP) return false;
  const pred = String(c.popis ?? "");
  const ted = String(popis ?? "");
  if (!(ted.length > pred.length && ted.startsWith(pred))) return false;
  return typZPopisu(pred) === c.typ;
}
